import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { sendGetRequest } from "../../../actions/app-actions";
import { getCalendarBusinessId } from "../../../utils/authHelper";
import { GOOGLE_CALENDAR_ID, OUTLOOK_CALENDAR_ID } from "../../../data/integration-app";
import { connect } from "react-redux";
import GoogleCalendarImgSrc from "../../../assets/calendar-integration/images/google_calendar.svg";
import OutlookLogoSrc from "../../../assets/icons/ms-outlook.svg";

const AddToCalendarModal = (props) => {
  const {
    data,
    showAddToCalendarModal,
    hideAddToCalendarModal,
    sendGetRequest,
    setIsLoading,
    onUpdateAddToCalendar,
    dataSet,
  } = props;

  const [calendars, setCalendars] = useState([]);
  const [addNewEventStatus, setAddNewEventStatus] = useState(
    data?.config?.add_new_event_status === "E"
  );
  const [selectedCalendar, setSelectedCalendar] = useState(
    data?.config?.selected_calendar_id
  );
  const [deleteEventStatus, setDeleteEventStatus] = useState(
    data?.config?.delete_event_status === "E"
  );
  // Track the SPECIFIC connection (id), not just the provider type
  const [appIntegrationId, setAppIntegrationId] = useState(data?.id);
  const [selectedConnId, setSelectedConnId] = useState(data?.id);
  const [selectedConnAppId, setSelectedConnAppId] = useState(data?.app_id);

  // Used to distinguish initial mount from account-switch renders
  const isFirstRender = useRef(true);

  // Refetch calendars whenever the specific account changes
  useEffect(() => {
    if (!addNewEventStatus) return;

    const isSwitch = !isFirstRender.current;
    isFirstRender.current = false;

    // Only reset sub-calendar selection when actively switching accounts (not on mount)
    if (isSwitch) {
      setCalendars([]);
      setSelectedCalendar(null);
      setDeleteEventStatus(false);
    }

    const requestUrl =
      parseInt(selectedConnAppId) === GOOGLE_CALENDAR_ID
        ? "google-list-calendars"
        : "outlook-list-calendars";

    setIsLoading(true);
    sendGetRequest({
      url: `/${requestUrl}/${selectedConnId}/${getCalendarBusinessId()}`,
      onSuccess: (response) => {
        const cals = response.calendars || [];
        setCalendars(cals);
        // After switching accounts, default to the first available sub-calendar
        if (isSwitch && cals.length > 0) {
          setSelectedCalendar(cals[0]?.id);
        }
        setIsLoading(false);
      },
      onError: () => setIsLoading(false),
    });
  }, [selectedConnId, addNewEventStatus]);

  const onUpdate = () => {
    // Always look up the config ID for the CURRENTLY selected connection
    const selectedConnData = dataSet.find((c) => c.id === selectedConnId);
    const configId = selectedConnData?.config?.id ?? null;

    onUpdateAddToCalendar({
      id: configId,
      add_new_event_status: addNewEventStatus ? "E" : "D",
      selected_calendar_id: selectedCalendar,
      delete_event_status: deleteEventStatus ? "E" : "D",
      app_integration_id: appIntegrationId,
    });
  };

  const getProviderLogo = (appId) => {
    return parseInt(appId) === GOOGLE_CALENDAR_ID ? GoogleCalendarImgSrc : OutlookLogoSrc;
  };

  const getEmail = (connection) => {
    try { return JSON.parse(connection?.meta_data)?.email || ""; } catch { return ""; }
  };

  return (
    <Modal
      className="ci-popup-modal"
      isOpen={showAddToCalendarModal}
      toggle={hideAddToCalendarModal}
      centered
    >
      <ModalBody>
        {/* Header */}
        <div className="ci-popup-head">
          <h2>Add new bookings to a calendar</h2>
          <button
            className="ci-popup-close"
            onClick={hideAddToCalendarModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Connected calendar options (radio) */}
        <div className="ci-popup-section">
          <h4>Choose calendar account</h4>
          {dataSet.map((connection, index) => {
            // Compare by specific connection ID — handles multiple same-provider accounts
            const isSelected = addNewEventStatus && connection.id === selectedConnId;
            return (
              <div
                key={connection.id || index}
                className={`ci-radio-row${isSelected ? " is-selected" : ""}`}
                onClick={() => {
                  setSelectedConnId(connection.id);
                  setSelectedConnAppId(connection.app_id);
                  setAppIntegrationId(connection.id);
                  setAddNewEventStatus(true);
                }}
              >
                <input
                  type="radio"
                  id={`calendar-${index}`}
                  name="add_new_event_status"
                  checked={isSelected}
                  onChange={() => {
                    setSelectedConnId(connection.id);
                    setSelectedConnAppId(connection.app_id);
                    setAppIntegrationId(connection.id);
                    setAddNewEventStatus(true);
                  }}
                />
                <label htmlFor={`calendar-${index}`}>
                  <img
                    src={getProviderLogo(connection?.app_id)}
                    alt="Calendar provider"
                  />
                  {getEmail(connection)}
                </label>
              </div>
            );
          })}

          {/* "Do not add" option */}
          <div
            className={`ci-radio-row${!addNewEventStatus ? " is-selected" : ""}`}
            onClick={() => setAddNewEventStatus(false)}
          >
            <input
              type="radio"
              id="notAdd"
              name="add_new_event_status"
              checked={!addNewEventStatus}
              onChange={() => setAddNewEventStatus(false)}
            />
            <label htmlFor="notAdd">Do not add new events to a calendar</label>
          </div>
        </div>

        {/* Calendar dropdown — only when adding events is enabled */}
        {addNewEventStatus && (
          <div className="ci-popup-section">
            <h4>Select specific calendar</h4>
            <select
              className="ci-popup-select"
              value={selectedCalendar}
              onChange={(e) => setSelectedCalendar(e.target.value)}
            >
              {calendars.map((calendar) => (
                <option key={calendar?.id} value={calendar?.id}>
                  {calendar?.summary}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Sync cancellations */}
        {addNewEventStatus && (
          <div className="ci-popup-section" style={{ marginBottom: 0 }}>
            <h4>Sync cancellations</h4>
            <div className="ci-check-row" style={{ borderBottom: 0, paddingBottom: 0 }}>
              <input
                id="delete_event_status"
                type="checkbox"
                checked={deleteEventStatus}
                onChange={(e) => setDeleteEventStatus(e.target.checked)}
              />
              <label htmlFor="delete_event_status">
                Deleting or declining an event in your calendar will also cancel
                it in MeetOcto.
              </label>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <button className="ci-popup-btn-cancel" onClick={hideAddToCalendarModal}>
          Cancel
        </button>
        <button className="ci-popup-btn-primary" onClick={onUpdate}>
          Save changes
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default connect(null, { sendGetRequest })(AddToCalendarModal);
